import React, { useState, useEffect } from 'react'
import { Link, useHistory } from 'react-router-dom'

import useQuery from '@/hooks/useQuery'
import { useDebouncedEffect } from '@/utils/useDebouncedEffect'
import Card from '@/shared/Card'
import TipTapWysiwyg from '@/shared/TipTapWysiwyg'
import { useGlobalState } from '@/state'
import PrimaryButton from '@/shared/Buttons/Primary'
import FeedbackImageUploader from '@/pages/Feedback/FeedbackImageUploader'

const CATEGORIES = [
  { key: 'feature_request', label: 'Feature Request' },
  { key: 'bug_report', label: 'Bug Report' }
]

// Strip tags from TipTap's HTML output so we can feed the searchable prose
// into the similar-feedback search without matching on markup noise.
const htmlToText = (html) => {
  if (!html) return ''
  const el = document.createElement('div')
  el.innerHTML = html
  return (el.textContent || el.innerText || '').trim()
}

const FeedbackNew = () => {
  const { getRequest, putpostRequest } = useQuery()
  const [, setToast] = useGlobalState('toast')
  const history = useHistory()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('feature_request')
  const [imageUrls, setImageUrls] = useState([])
  // TipTap reads `content` only at creation; bumping `idx` triggers its
  // clearContent effect so the editor visually empties on submit.
  const [descriptionResetIdx, setDescriptionResetIdx] = useState(1)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState(null)
  const [similar, setSimilar] = useState([])

  const searchSeed = `${title} ${htmlToText(description)}`.trim()

  useDebouncedEffect(() => {
    if (searchSeed.length < 4) { setSimilar([]); return }
    getRequest('/api/v3/feedbacks', { search: searchSeed, sort: 'most_voted' }, (err, jsonData) => {
      if (err) return
      setSimilar((jsonData.feedbacks || []).slice(0, 5))
    })
  }, 300, [searchSeed])

  const submit = () => {
    if (!title.trim()) { setErrors({ title: 'Title is required' }); return }

    setLoading(true)
    setErrors(null)
    putpostRequest('/api/v3/feedbacks', 'POST', {
      feedback: { title, description, category },
      image_urls: imageUrls
    }, (err, jsonData) => {
      setLoading(false)
      if (err) { setErrors(typeof err === 'object' ? err : { base: err }); return }
      setTitle('')
      setDescription('')
      setImageUrls([])
      setDescriptionResetIdx((i) => i + 1)
      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-white text-gray-800">Feedback submitted!</p>
        <p className="mt-1 text-sm dark:text-gray-100 text-gray-500">Thanks for helping improve CCC.</p>
      </div>)
      history.push(`/feedback/${jsonData.feedback.slug}`)
    })
  }

  return (
    <div className='max-w-2xl mx-auto'>
      <Card title='Submit Feedback' footer={
        <div className='flex justify-end'>
          <PrimaryButton onClick={submit} loading={loading} text='Submit' />
        </div>
      }>
        <div className='space-y-4'>
          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1'>Category</label>
            <div className='flex gap-2'>
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  type='button'
                  onClick={() => setCategory(c.key)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    category === c.key
                      ? 'bg-cccblue text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1'>Title</label>
            <input
              type='text'
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={`block w-full ${errors?.title ? 'border-red-500' : ''}`}
              placeholder='Short summary of your feedback'
              maxLength={200}
            />
            {errors?.title && <p className='mt-1 text-sm text-red-600'>{errors.title}</p>}
          </div>

          {similar.length > 0 && (
            <div className='rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-900/50 p-3'>
              <p className='text-xs font-semibold text-amber-800 dark:text-amber-200 mb-2'>
                Before you post — is this similar to something that already exists?
              </p>
              <ul className='space-y-1'>
                {similar.map((f) => (
                  <li key={f.id}>
                    <Link
                      to={`/feedback/${f.slug}`}
                      className='flex items-start gap-2 py-1 text-sm text-gray-800 dark:text-gray-100 hover:text-cccblue'
                    >
                      <span className='mt-0.5 inline-flex items-center justify-center min-w-[2.5rem] px-1.5 py-0.5 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs font-semibold'>
                        {f.votesCount}
                      </span>
                      <span className='flex-1'>{f.title}</span>
                      <span className='text-xs text-gray-500 dark:text-gray-400 capitalize whitespace-nowrap'>{f.status.replace('_', ' ')}</span>
                    </Link>
                  </li>
                ))}
              </ul>
              <p className='text-xs text-gray-500 dark:text-gray-400 mt-2'>
                Click one to vote on it instead.
              </p>
            </div>
          )}

          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1'>Description</label>
            <TipTapWysiwyg
              html={description}
              updateHtml={setDescription}
              placeholder='Describe the feature or bug in detail...'
              idx={descriptionResetIdx}
              onSubmitShortcut={submit}
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1'>Images</label>
            <FeedbackImageUploader urls={imageUrls} onChange={setImageUrls} />
            <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>Up to 5 screenshots or mockups.</p>
          </div>

          {errors?.base && <p className='text-sm text-red-600'>{errors.base}</p>}
        </div>
      </Card>
    </div>
  )
}

export default FeedbackNew
